import React, { useState, useEffect } from "react";
import {
  Select,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  MenuItem,
  Button,
  CircularProgress,
  Box,
  TextField,
  InputLabel,
  FormControl,
  Typography,
  Pagination,
  Collapse,
} from "@mui/material";
import GerenciarUsuariosCardUser from "../GerenciarUsuariosCardUser";
import { API_URLS } from "../../config/apiUrls";
import { API_IBGE } from "../../config/apiIBGE";
import FilterListIcon from "@mui/icons-material/FilterList";

interface GerenciarUsuariosListaProps {
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
}

interface User {
  id: string;
  financialRegister: string | null;
  name: string;
  email: string;
  phone: string;
  ufId: number;
  cityId: string;
  createdAt: string;
  isDeleted: boolean;
  city: {
    id: number;
    name: string;
  };
}

const GerenciarUsuariosLista: React.FC<GerenciarUsuariosListaProps> = ({
  token,
  role,
}) => {
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [filter, setFilter] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<string>("asc");
  const [filterBy, setFilterBy] = useState<keyof User>("name");

  const [filterByCity, setFilterByCity] = useState<string>("");
  const [filterByState, setFilterByState] = useState<string>("");
  const [filterByStateSigla, setFilterByStateSigla] = useState<string>("");
  const [cities, setCities] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);

  // Novos estados para o filtro de data
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [appliedFilter, setAppliedFilter] = useState<boolean>(false);

  const [page, setPage] = useState<number>(1); // Página atual
  const [itemsPerPage] = useState<number>(25); // Itens por página

  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Função para mudar a página (inalterada)
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setPage(page);
    window.scrollTo(0, 0);
  };

  const handleUF = (id: string, sigla: string) => {
    setFilterByState(id);
    setFilterByStateSigla(sigla);
  };

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(`${API_IBGE.estados}`);
        const data = await response.json();
        const sortedStates = data.sort(
          (a: { nome: string }, b: { nome: string }) => {
            return a.nome.localeCompare(b.nome); // Ordem alfabética
          },
        );
        setStates(sortedStates);
      } catch (err) {
        setError("Erro ao carregar estados");
      }
    };

    const fetchUsuarios = async () => {
      try {
        const response = await fetch(API_URLS.users, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Falha ao carregar os usuários.");
        }

        const data = await response.json();
        setUsuarios(data);
      } catch (err) {
        setError("Erro ao carregar usuários");
      } finally {
        setLoading(false);
      }
    };

    fetchStates();
    fetchUsuarios();
  }, [token]);

  useEffect(() => {
    if (filterByState) {
      const fetchCities = async () => {
        try {
          const response = await fetch(
            `${API_IBGE.cidades}/${filterByStateSigla}`,
          );
          const data = await response.json();
          setCities(data);
        } catch (err) {
          setError("Erro ao carregar cidades");
        }
      };

      fetchCities();
    } else {
      setCities([]);
    }
  }, [filterByState]);

  // Função para filtrar usuários (inalterada)
  const filteredUsers = usuarios.filter((user) => {
    if (user.isDeleted) return false;

    const filterMatch =
      filterBy === "name"
        ? user.name && user.name.toLowerCase().includes(filter.toLowerCase())
        : filterBy === "email"
          ? user.email &&
            user.email.toLowerCase().includes(filter.toLowerCase())
          : true;

    const cityMatch =
      filterByCity === "" ||
      (user.cityId && user.cityId.includes(filterByCity));

    const stateMatch =
      filterByState === "" ||
      (user.ufId && user.ufId.toString() === filterByState.toString());

    // Filtragem por data de criação (createdAt)
    const createdAtDate = new Date(user.createdAt);
    let dateMatch = true;

    if (startDate) {
      const start = new Date(startDate);
      start.setUTCHours(0, 0, 0, 0); // Set time to beginning of the day
      dateMatch = dateMatch && createdAtDate >= start;
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setUTCHours(23, 59, 59, 999); // Set time to end of the day
      dateMatch = dateMatch && createdAtDate <= end;
    }

    return filterMatch && cityMatch && stateMatch && dateMatch;
  });

  // Função para ordenar usuários (inalterada)
  const sortedUsers = filteredUsers.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();

    // Default sort by createdAt in descending order (most recent first)
    if (dateA !== dateB) {
      return dateB - dateA;
    }

    const valueA = a[filterBy] ? String(a[filterBy]).toLowerCase() : "";
    const valueB = b[filterBy] ? String(b[filterBy]).toLowerCase() : "";

    if (sortDirection === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });

  const applyFilter = () => {
    setAppliedFilter(true);
    setPage(1); // Resetar para a primeira página ao aplicar filtro
  };

  const applySort = (direction: string) => {
    setSortDirection(direction);
  };

  const handleDeleteUser = (userId: string) => {
    setUsuarios((prevUsuarios) =>
      prevUsuarios.filter((user) => user.id !== userId),
    );
  };

  const clearFilters = () => {
    setFilter("");
    setFilterBy("name");
    setSortDirection("asc");
    setFilterByCity("");
    setFilterByState("");
    setFilterByStateSigla("");
    setStartDate(""); // Limpa a data de início
    setEndDate(""); // Limpa a data de fim
    setAppliedFilter(false);
    setPage(1); // Resetar para a primeira página ao limpar os filtros
  };

  // Calcular o índice de início e fim com base na página atual e itens por página
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Pegar apenas a quantidade de usuários correspondente à página
  const usersToDisplay = sortedUsers.slice(startIndex, endIndex);

  // Atualiza o número total de usuários filtrados (antes da paginação)
  const filteredUsersCount = filteredUsers.length;

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Erro: {error}</div>;
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Typography gutterBottom variant="h2">
        Usuários
      </Typography>
      <Box mb={2}>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
        </Button>
      </Box>
      {/* COLLAPSE QUE ENVOLVE OS FILTROS ADICIONAIS */}
      <Collapse in={showFilters}>
        {/* Campo de filtro */}
        <Box sx={{ marginBottom: 2 }}>
          {/* Filtro de intervalo de datas - SUBSTITUINDO GRID */}
          {role !== "ANALYST" && (
            <Box mb={3} sx={{ display: "flex", gap: 2 }}>
              {/* Data de Início */}
              <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
                <TextField
                  label="Data de Início"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              {/* Data de Fim */}
              <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
                <TextField
                  label="Data de Fim"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            </Box>
          )}

          <TextField
            label={`Filtrar por ${filterBy === "name" ? "Nome" : "E-mail"}`}
            variant="outlined"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            fullWidth
          />
        </Box>

        {/* Filtros de Ordenação e Filtragem por tipo (Nome/Email) */}
        <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Filtrar por</InputLabel>

            <Select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value as keyof User)}
              label="Filtrar por"
            >
              <MenuItem value="name">Nome</MenuItem>
              <MenuItem value="email">E-mail</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Ordenação</InputLabel>
            <Select
              value={sortDirection}
              onChange={(e) => applySort(e.target.value)}
              label="Ordenação"
            >
              <MenuItem value="asc">Ascendente</MenuItem>
              <MenuItem value="desc">Descendente</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Campos de filtro por cidade e estado */}
        {role !== "PARTNER" && (
          <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filterByState}
                onChange={(e) => {
                  const selectedState = states.find(
                    (state) => state.id === e.target.value,
                  );
                  if (selectedState) {
                    handleUF(selectedState.id, selectedState.sigla);
                  }
                }}
                label="Estado"
              >
                <MenuItem value="">
                  <em>Todos os Estados</em>
                </MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.nome} ({state.sigla})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Cidade</InputLabel>
              <Select
                value={filterByCity}
                onChange={(e) => setFilterByCity(e.target.value)}
                label="Cidade"
              >
                <MenuItem value="">
                  <em>Todas as Cidades</em>
                </MenuItem>
                {Array.isArray(cities) &&
                  cities.map((city) => (
                    <MenuItem key={city.codigo_ibge} value={city.codigo_ibge}>
                      {city.nome}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box sx={{ marginBottom: 2 }}>
          <Button variant="contained" onClick={applyFilter}>
            Aplicar Filtro
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearFilters}
            sx={{ marginLeft: 2 }}
          >
            Limpar Filtros
          </Button>
        </Box>
      </Collapse>

      {/* Exibição do número total de usuários filtrados */}
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="h6">
          Total de usuários: {filteredUsersCount}
        </Typography>
      </Box>

      {/* Exibição dos usuários filtrados e ordenados */}
      <Box>
        {usersToDisplay.map((user) => (
          <GerenciarUsuariosCardUser
            key={user.id}
            user={user}
            token={token}
            role={role}
            onDelete={handleDeleteUser}
          />
        ))}
      </Box>

      {/* Paginação */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={Math.ceil(filteredUsersCount / itemsPerPage)}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default GerenciarUsuariosLista;
